import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEdit, FaTrash, FaUpload } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";

function ReviewPhase2_0() {
  const [project, setProject] = useState({});
  const [reviews, setReviews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [newReview, setNewReview] = useState({ faculty_name: "", review: "", rating: 1 });
  const [projectId, setProjectId] = useState(null);

  const nav = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.PRC?.currentUser || {});
  const backendURL = process.env.REACT_APP_backend_url;

  useEffect(() => {
    const fetchProject = async () => {
      const path = location.pathname;
      const id = path.slice(14);
      setProjectId(id);
      try {
        const token = sessionStorage.getItem("Token");
        const response = await axios.get(`${backendURL}/get_review/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(response.data.project);
        setReviews(response.data.reviews || []);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };
    fetchProject();
  }, [location]);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("Token")}`
    }
  });

  const handleAddReview = async () => {
    try {
      await axios.post(`${backendURL}/add_review`, {
        project_id: project.project_id,
        department: project.department,
        ...newReview
      }, authHeaders());
      setNewReview({ faculty_name: "", review: "", rating: 1 });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding review:", error);
    }
  };

  const handleEditReview = async () => {
    try {
      await axios.put(`${backendURL}/edit_review`, {
        project_id: project.project_id,
        department: project.department,
        review_id: editingReviewId,
        ...newReview
      }, authHeaders());
      setEditingReviewId(null);
      setIsModalOpen(false);
      
    } catch (error) {
      console.error("Error editing review:", error);
    }
  };

  const handleDeleteReview = async (review_id) => {
    try {
      await axios.delete(`${backendURL}/delete_review/${review_id}`, authHeaders());
      const refreshed = await axios.get(`${backendURL}/get_review/${projectId}`, authHeaders());
      setReviews(refreshed.data.reviews || []);
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  const handlePublishProject = async () => {
    try {
      await axios.post(`${backendURL}/publish_project/${projectId}`, {}, authHeaders());
      alert("Project has been published successfully!");
      window.location.href = "https://dev-projecthub.vjstartup.com/reviewPhaseForPRC";
    } catch (error) {
      console.error("Error publishing project:", error);
    }
  };

  return (
    <div className="container py-5" style={{ backgroundColor: "#f5f9ff", minHeight: "100vh" }}>
      <div className="text-start mb-4">
        <h2 className="fw-bold">Welcome to the Department of {currentUser.department || "Unknown"}</h2>
      </div>

      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h3 className="card-title">{project.title}</h3>
          <h6 className="card-subtitle mb-2 text-muted">by {project.staff_name}</h6>
          <p className="card-text">{project.description || project.abstract}</p>
        </div>
      </div>

      <div className="mb-3 d-flex gap-2">
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingReviewId(null);
            setNewReview({ faculty_name: "", review: "", rating: 1 });
            setIsModalOpen(true);
          }}
        >
          + Add Review
        </button>
        <button className="btn btn-success" onClick={handlePublishProject}>
          <FaUpload className="me-1" /> Publish Project
        </button>
      </div>

      {reviews.map((review) => (
        <div key={review.review_id || uuidv4()} className="card mb-3 shadow-sm">
          <div className="card-body">
            <h5 className="card-title">{review.faculty_name}</h5>
            <div className="mb-2">
              <span className="badge bg-warning text-dark me-2">
                Rating: {review.rating} / 5
              </span>
              <span className="text-muted">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span>
            </div>
            <div className="alert alert-secondary mt-2 mb-3">
              <strong>Feedback:</strong> {review.reviews}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                title="Edit"
                onClick={() => {
                  setEditingReviewId(review.review_id);
                  setNewReview({
                    faculty_name: review.faculty_name,
                    review: review.review,
                    rating: review.rating
                  });
                  setIsModalOpen(true);
                }}
              >
                <FaEdit className="me-1" /> Edit
              </button>
              <button
                className="btn btn-outline-danger btn-sm"
                title="Delete"
                onClick={() => handleDeleteReview(review.review_id)}
              >
                <FaTrash className="me-1" /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal show fade d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingReviewId ? "Edit Review" : "Add a Review"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <input
                  className="form-control mb-3"
                  type="text"
                  placeholder="Faculty Name"
                  value={newReview.faculty_name}
                  onChange={(e) => setNewReview({ ...newReview, faculty_name: e.target.value })}
                />
                <textarea
                  className="form-control mb-3"
                  rows="4"
                  placeholder="Write your review here..."
                  value={newReview.review}
                  onChange={(e) => setNewReview({ ...newReview, review: e.target.value })}
                />
                <label className="form-label">Rating</label>
                <select
                  className="form-select"
                  value={newReview.rating}
                  onChange={(e) => setNewReview({ ...newReview, rating: parseInt(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={editingReviewId ? handleEditReview : handleAddReview}
                >
                  {editingReviewId ? "Update" : "Submit"}
                </button>
                <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewPhase2_0
